<template>
	<div class="container" id="risk-matrix">
		<div class="dashboardriskdisplay">
			<div
				v-for="category in categories"
				:key="category.key"
				class="riskpointdashboard"
				:class="{ 'category-disabled': isCategoryDisabled(category.key) }"
				@click="selectCategory(category.key)"
				>
				<v-col class="createriskdashboard">
					<div style="display:flex; align-items:center; justify-content:space-between;">
						<h3 class="category-title">{{ category.label }}</h3>
						<v-btn
							v-if="selectedCategory === category.key"
							x-small
							text
							color="primary"
							@click.stop="clearCategoryFilter"
						>Clear filter</v-btn>
					</div>

					<v-select
					:items="subCategories[category.key]"
					v-model="selectedSubCategory_local[category.key]"
					label="Select a Risk Type"
					dense
					outlined
					clearable
					class="matrix-select"
					:disabled="isCategoryDisabled(category.key)"
					@click.stop
					@change="onSubCategoryChange(category.key)"
					></v-select>

					<div class="risk-chart-wrapper" style="margin: -50px -30px -50px 50px;">
					<RiskmatrixChartByCategory
						:key="category.key + '-' + (selectedSubCategory_local[category.key] || 'none')"
						:offsetValue="0"
						:points="pointsByCategory[category.key]"
					/>
					<div class="horizontal-text">Probability</div>
					</div>
				</v-col>
			</div>
		</div>
	</div>
</template>

<script>
import RiskmatrixChartByCategory from "./RiskmatrixChartByCategory.vue"
import { riskAPI } from "../api/csr/risk"
import LoadingState from "../consts/LoadingState"
import { mapGetters } from "vuex"

export default {
	data() {
		return {
			categories: [
				{ key: "Governance & Management", label: "GOVERNANCE & MANAGEMENT" },
				{ key: "People & Human Rights", label: "PEOPLE & HUMAN RIGHTS" },
				{ key: "Environment & Resources", label: "ENVIRONMENT & RESOURCES" },
				{ key: "Operations & Supply Chains", label: "OPERATIONS & SUPPLY CHAINS" },
			],

			subCategories: {
				"Governance & Management": [
					"All",
					"Corruption",
					"Country",
					"Financial",
					"Quality Assurance Management",
					"Management",
				],
				"People & Human Rights": [
					"All",
					"Gender Rights",
					"Human Rights",
					"Labour",
				],
				"Environment & Resources": [
					"All",
					"Environment",
					"Technology",
					"Water Use",
				],
				"Operations & Supply Chains": [
					"All",
					"Market",
					"Production",
					"Quality",
					"Supply Chains",
				],
			},

			selectedSubCategory_local: {
				"Governance & Management": null,
				"People & Human Rights": null,
				"Environment & Resources": null,
				"Operations & Supply Chains": null,
			},

			rows: [],

			allPointsByCategory: {
				"Governance & Management": [],
				"People & Human Rights": [],
				"Environment & Resources": [],
				"Operations & Supply Chains": [],
			},

			pointsByCategory: {
				"Governance & Management": [],
				"People & Human Rights": [],
				"Environment & Resources": [],
				"Operations & Supply Chains": [],
			},
		}
	},

	components: {
		RiskmatrixChartByCategory,
	},

	computed: {
		...mapGetters([
			"selectedId",
			"selectedYear",
			"selectedTitle",
			"selectedDescription",
			"selectedCategory",
		]),
	},

	mounted() {
		if (this.selectedId) {
			this.loadRisk(this.selectedId)
		}
	},

	watch: {
		selectedId: {
			immediate: true,
			handler(newId) {
				if (newId) {
					this.loadRisk()
				}
			}
		},
		selectedTitle(newVal) {
			console.log("Selected Title changed:", newVal)
		},
		selectedDescription(newVal) {
			console.log("Selected Description changed:", newVal)
		},
	},

	methods: {
		selectCategory(categoryKey) {
			if (this.selectedCategory === categoryKey) return 

			Object.keys(this.selectedSubCategory_local).forEach((key) => {
				this.selectedSubCategory_local[key] = null
				if (key !== categoryKey) this.pointsByCategory[key] = []
			})

			this.showAllCategoryPoints(categoryKey)
			this.$store.commit("setSelectedCategory", categoryKey)
			this.$store.commit("setSelectedSubCategory", null)
		},
		renumberPoints(points) {
			const riskMap = new Map()
			let counter = 0
			return points.map((point) => {
				if (!riskMap.has(point.originalRiskId)) {
					counter++
					riskMap.set(point.originalRiskId, counter)
				}
				return { ...point, risk: riskMap.get(point.originalRiskId) }
			})
		},

		showAllCategoryPoints(categoryKey) {
			const selectedSub = this.selectedSubCategory_local[categoryKey]
			const categoryRiskTypes = (this.subCategories[categoryKey] || []).filter(
				(type) => type !== "All"
			)
			const all = this.allPointsByCategory[categoryKey] || []

			const filtered = all.filter((point) => {
				const pointType = (point.subCategory || "").trim().toLowerCase()

				if (selectedSub && selectedSub !== "All") {
					return pointType === selectedSub.trim().toLowerCase()
				}

				return categoryRiskTypes.some(
					(type) => type.trim().toLowerCase() === pointType
				)
			})

			this.pointsByCategory[categoryKey] = this.renumberPoints(filtered)
		},
		mapToGridCoordinates(c, p) {
			let cons = parseInt(c, 10) || 1
			let prob = parseInt(p, 10) || 1

			if (cons <= 5) cons = (cons - 1) * 5 + 3
			if (prob <= 5) prob = (prob - 1) * 5 + 3

			const gridRow = Math.max(0, Math.min(24, 25 - cons))
			const gridCol = Math.max(0, Math.min(24, prob - 1))

			return { row: gridRow, col: gridCol }
		},
		normalizeCategory(rawCategory) {
			if (!rawCategory) return null
			const normalized = rawCategory.trim().toLowerCase()
			const match = this.categories.find(
				(c) => c.key.trim().toLowerCase() === normalized
			)
			return match ? match.key : null
		},

		addPoint(category, consequence, probability, riskIndex, subCategory) {
			const c = parseInt(consequence, 10) || 1
			const p = parseInt(probability, 10) || 1

			const rowI = Math.max(0, Math.min(24, Math.abs(c - 25)))
			const colJ = Math.max(0, Math.min(24, Math.abs(p - 1)))

			const point = {
				x: rowI,
				y: colJ,
				risk: riskIndex,
				originalRiskId: riskIndex, 
				subCategory: subCategory,
			}

			if (!this.allPointsByCategory[category]) {
				this.allPointsByCategory[category] = []
			}
			this.allPointsByCategory[category].push(point)
		},

		onSubCategoryChange(categoryKey) {
			const selected = this.selectedSubCategory_local[categoryKey]
			const isAll = selected === "All"

			Object.keys(this.selectedSubCategory_local).forEach((key) => {
				if (key !== categoryKey) {
					this.selectedSubCategory_local[key] = null
					this.pointsByCategory[key] = []
				}
			})

			this.showAllCategoryPoints(categoryKey)

			this.$store.commit("setSelectedCategory", selected ? categoryKey : null)
			this.$store.commit("setSelectedSubCategory", isAll ? null : selected)
		},

		isCategoryDisabled(categoryKey) {
			return this.selectedCategory !== null && this.selectedCategory !== categoryKey
		},

		clearCategoryFilter() {
			Object.keys(this.selectedSubCategory_local).forEach((key) => {
				this.selectedSubCategory_local[key] = null
				this.pointsByCategory[key] = []
			})
			this.$store.commit("setSelectedCategory", null)
			this.$store.commit("setSelectedSubCategory", null)
		},

		loadRisk() {
			if (!this.selectedId) return

			Object.keys(this.allPointsByCategory).forEach((key) => {
				this.allPointsByCategory[key] = []
				this.pointsByCategory[key] = []
			})

			this.$store.dispatch(
				"loading/" + LoadingState.ACTION_START_LOADING,
				null,
				{ root: true }
			)

			riskAPI
				.getAll(this.selectedId)
				.then((data) => {
					let newdata = data && data[0] ? data[0] : []
					const categoryCounters = {}

					for (let entry of newdata) {
						const riskType = (entry.TypesOfRisk || "").trim()

						let targetCategoryKey = null
						for (const [catKey, types] of Object.entries(this.subCategories)) {
							if (types.some((t) => t.trim().toLowerCase() === riskType.toLowerCase())) {
								targetCategoryKey = catKey
								break
							}
						}

						if (!targetCategoryKey) {
							targetCategoryKey = this.normalizeCategory(entry.Category)
						}

						if (!targetCategoryKey) continue

						categoryCounters[targetCategoryKey] = (categoryCounters[targetCategoryKey] || 0) + 1
						const riskDisplayNumber = categoryCounters[targetCategoryKey]

						const initialCons = entry.FirstConsequence != null ? entry.FirstConsequence : entry.Consequence
						const initialProb = entry.FirstProbability != null ? entry.FirstProbability : entry.Probability

						this.addPoint(
							targetCategoryKey,
							initialCons,
							initialProb,
							riskDisplayNumber,
							entry.TypesOfRisk
						)

						if (
							entry.UpdatedConsequence != null &&
							entry.UpdatedProbability != null
						) {
							this.addPoint(
								targetCategoryKey,
								entry.UpdatedConsequence,
								entry.UpdatedProbability,
								riskDisplayNumber,
								entry.TypesOfRisk
							)
						}
					}
					Object.keys(this.allPointsByCategory).forEach((key) => {
						this.showAllCategoryPoints(key)
					})
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_END_LOADING,
						null,
						{ root: true }
					)
				})
				.catch((error) => {
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_SET_ERROR,
						error,
						{ root: true }
					)
				})
		}
	},
}
</script>

<style scoped>
.container {
	display: flex;
	flex-direction: column;
	align-items: center;
	margin-left: 0px;
	margin-top: 0px;
	width: 100%;
}

.dashboardriskdisplay {
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	column-gap: 40px;
	width: 100%;
	max-width: 1400px;
	margin: 0 auto;
}

.category-title {
	text-align: center;
	margin-bottom: 15px;
}

.riskpointdashboard {
	display: flex;
	flex-direction: column;
	height: auto;               
	min-height: 560px;          
	width: 100%;
	max-width: 850px;
	justify-self: center;
	border: 1px solid #ddd;
	border-radius: 10px;
	align-items: center;
	justify-content: flex-start;
	box-sizing: border-box;
	overflow: visible;
	cursor: pointer;
}

.matrix-select {
	max-width: 300px;
	width: 100%;
	justify-self: center;
}

.horizontal-text {
	margin-top: 10px;
	margin-bottom: 4px;
	color: #217b7f;
	letter-spacing: 10px;
	font-weight: 1000;
	font-size: 16px;
}

.category-disabled {
  opacity: 0.4;
  pointer-events: none;
}

@media (max-width: 900px) {
	.dashboardriskdisplay {
		grid-template-columns: 1fr;
		gap: 50px;
	}

	.riskpointdashboard {
		max-width: 720px;
	}
}
</style>